import { useState, useEffect, DragEvent, ChangeEvent } from 'react';
import { Upload } from 'lucide-react';
import DocumentsUploadModal from './DocumentsUploadModal';
import DocumentsMultiUploadModal from './DocumentsMultiUploadModal';
import type { UploadDocumentResponse } from '../../../api/types';

export interface UploadCompletionPayload {
    uploads: UploadDocumentResponse[];
}

interface FileUploadProps {
    onUploadComplete: (payload: UploadCompletionPayload) => void;
    businessId?: string;
    availableTagLabels?: string[];
    onLabelsChanged?: () => void;
    externalFiles?: File[];
    onExternalFilesHandled?: () => void;
}

const SUPPORTED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.csv', '.xls', '.xlsx'];

const getAcceptedFiles = (files: FileList | null): File[] => {
    if (!files) return [];
    return Array.from(files).filter((f) => {
        const ext = '.' + f.name.split('.').pop()?.toLowerCase();
        return SUPPORTED_EXTENSIONS.some((e) => ext === e);
    });
};

const FileUpload: React.FC<FileUploadProps> = ({
    onUploadComplete,
    businessId,
    availableTagLabels = [],
    onLabelsChanged,
    externalFiles = [],
    onExternalFilesHandled,
}) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showMultiUploadModal, setShowMultiUploadModal] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [dragOver, setDragOver] = useState(false);

    const openAcceptedFiles = (files: File[]) => {
        if (files.length === 0) return;
        if (files.length === 1) {
            setUploadedFile(files[0]);
            setShowUploadModal(true);
        } else {
            setUploadedFiles(files);
            setShowMultiUploadModal(true);
        }
    };

    useEffect(() => {
        if (externalFiles.length === 0) return;
        openAcceptedFiles(externalFiles);
        onExternalFilesHandled?.();
    }, [externalFiles, onExternalFilesHandled]);

    const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const accepted = getAcceptedFiles(event.target.files);
        openAcceptedFiles(accepted);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(false);
        const accepted = getAcceptedFiles(event.dataTransfer.files);
        openAcceptedFiles(accepted);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleModalUploadComplete = (result: UploadDocumentResponse) => {
        setShowUploadModal(false);
        setUploadedFile(null);
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        onUploadComplete({ uploads: [result] });
    };

    const handleModalClose = () => {
        setShowUploadModal(false);
        setUploadedFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleMultiModalClose = () => {
        setShowMultiUploadModal(false);
        setUploadedFiles([]);
        const input = document.querySelector<HTMLInputElement>('input[type="file"]');
        if (input) input.value = '';
    };

    const handleMultiModalComplete = (results: UploadDocumentResponse[]) => {
        setShowMultiUploadModal(false);
        setUploadedFiles([]);
        const input = document.querySelector<HTMLInputElement>('input[type="file"]');
        if (input) input.value = '';
        onUploadComplete({ uploads: results });
    };

    return (
        <>
            {showUploadModal && uploadedFile && (
                <DocumentsUploadModal
                    file={uploadedFile}
                    onClose={handleModalClose}
                    onSaveComplete={handleModalUploadComplete}
                    businessId={businessId}
                    availableTagLabels={availableTagLabels}
                    onLabelsChanged={onLabelsChanged}
                />
            )}
            {showMultiUploadModal && uploadedFiles.length > 0 && (
                <DocumentsMultiUploadModal
                    files={uploadedFiles}
                    onClose={handleMultiModalClose}
                    onSaveComplete={handleMultiModalComplete}
                    businessId={businessId}
                    availableTagLabels={availableTagLabels}
                    onLabelsChanged={onLabelsChanged}
                />
            )}

            <div
                className={`relative mb-3 flex h-full min-h-[220px] flex-col items-center justify-center rounded-lg border border-[#E9E9E9] bg-white p-8 py-10 text-center transition-all md:min-h-[240px] ${dragOver ? 'border-2 border-blue-500 border-dashed' : ''
                    }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <>
                    <p className="mb-2 text-xl font-semibold text-gray-800">
                        Drag your file(s) here to upload
                    </p>
                    <p className="mb-5 text-gray-500">
                        Supported files: PDF, JPG, PNG, HEIC, CSV, XLS, XLSX
                    </p>

                    <label className="cursor-pointer">
                        <input type="file" hidden multiple accept={SUPPORTED_EXTENSIONS.join(',')} onChange={handleFileSelect} />
                        <div className="button-1 flex items-center gap-2 !bg-[#5B5DE4] !border-[#5B5DE4] !text-white hover:!bg-[#4f51cf]">
                            Select Files
                            <Upload className="w-4 h-4" />
                        </div>
                    </label>
                </>
            </div>
        </>
    );
};

export default FileUpload;
